import {Injectable, signal} from '@angular/core';
import {ViewContext, ViewContextEntity} from './view-context.models';

const STORAGE_KEY = 'pantry.dialog.viewContextEnabled';
const MAX_ENTITIES = 5;
const MAX_LABEL_LENGTH = 60;

interface Registration {
  id: string;
  depth: number;
  route: string;
  getEntities: () => ViewContextEntity[];
}

export function truncateLabel(label: string, max: number = MAX_LABEL_LENGTH): string {
  const trimmed = (label ?? '').trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

function readStoredEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === null ? true : raw === 'true';
}

/**
 * Tracks what the user is currently looking at, so the dialog assistant can be given
 * lightweight, explicitly-declared context about the active page.
 *
 * Registrations are evaluated fresh on every `snapshot()` call (not cached), since
 * context must reflect the page at the moment of each dialog turn, not the moment of
 * registration.
 */
@Injectable({providedIn: 'root'})
export class ViewContextService {
  private readonly _registrations = signal<Registration[]>([]);
  private readonly _enabled = signal<boolean>(readStoredEnabled());
  readonly enabled = this._enabled.asReadonly();

  private nextDepth = 0;

  /**
   * Registers a page's context. Returns a registration id; callers should prefer the
   * `provideViewContext()` helper, which wires cleanup automatically.
   */
  register(route: string, getEntities: () => ViewContextEntity[]): string {
    const id = crypto.randomUUID();
    const registration: Registration = {id, depth: this.nextDepth++, route, getEntities};
    this._registrations.update(regs => [...regs, registration]);
    return id;
  }

  unregister(id: string): void {
    this._registrations.update(regs => regs.filter(r => r.id !== id));
  }

  setEnabled(value: boolean): void {
    this._enabled.set(value);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(value));
    }
  }

  /**
   * Fresh snapshot of the current view context, or null when disabled or nothing is
   * registered. `route` comes from the innermost (most recently registered) active
   * registration; entities are concatenated innermost-first and capped at 5.
   */
  snapshot(): ViewContext | null {
    if (!this._enabled()) return null;

    const regs = this._registrations();
    if (regs.length === 0) return null;

    const sorted = [...regs].sort((a, b) => b.depth - a.depth);
    const route = sorted[0].route;
    const entities = sorted
      .flatMap(r => r.getEntities() ?? [])
      .slice(0, MAX_ENTITIES)
      .map(e => ({...e, label: truncateLabel(e.label)}));

    return {route, entities};
  }

  /** Short human-readable summary for the panel header indicator, e.g. "Valflora Halbrahm". */
  describeCurrent(): string | null {
    const snapshot = this.snapshot();
    if (!snapshot || snapshot.entities.length === 0) return null;
    return snapshot.entities.map(e => e.label).join(', ');
  }
}
